import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import { navigate } from "@reach/router";
import api from "../api";
import moment from "moment";
import { AxiosContext } from "./AxiosContext";
import { handelError } from "../helper/utils";

export const SwapContext = createContext({
  abortSwap: async (swapId) => {},
  swap: null,
  fetchSwapById: async (swapId) => {},
  abortSwapLoading: false,
});

export const useSwaps = () => useContext(SwapContext);

export const SwapProvider = ({ children }) => {
  const [swap, setSwap] = useState();
  const [abortSwapLoading, setAbortSwapLoading] = useState();
  const [state, setstate] = useState({
    data: null,
    direction: "descending",
    count: null,
    pages: 0,
    column: null,
    page: 0,
  });

  const [batteryChargingStatistic, setBatteryChargingStatistic] = useState();
  const [selectedUsername, setSelectedUsername] = useState();
  const [selectedUserGroupName, setSelectedUserGroupName] = useState();

  const [swap_state, setBatteryStateSearch] = useState();
  const [battery_type, setSelectedBatteryType] = useState();
  const [sp_serial_number, setSelectedStation] = useState();
  const [isLoading, setIsLoading] = useState();
  const [exportData, setExportData] = useState();
  const [lower, setLower] = useState();
  const [upper, setUpper] = useState();
  const [location, setSelectedLocation] = useState();
  const [IsSwapExportIsLoading, setSwapExportIsLoading] = useState();

  const [isLoadingLoadMore, setIsLoadingLoadMore] = useState();
  const [fetchingSwapSummaryReport, setFetchingSwapSummaryReport] = useState();

  const { axiosInstance } = useContext(AxiosContext);

  const fetchSwaps = async (station_id, column, direction) => {
    try {
      setIsLoading(true);
      setstate({ ...state, data: null, count: null });

      const params = {
        limit: 50,
        state: swap_state,
        created_by: selectedUsername,
        user_group_name: selectedUserGroupName,
        battery_type,
        station: station_id || sp_serial_number,
        lower: lower ? moment(lower, "DD/MM/YYYY").toDate().getTime() : null,
        upper: upper
          ? moment(upper, "DD/MM/YYYY").toDate().getTime() + 86400000
          : null,
        location_id: location,
      };

      if (!column) {
        const { data } = await axiosInstance.get("swap_actions", {
          params,
        });
        setstate({
          ...state,
          data: data.data,
          count: data.count,
          pages: data.pages,
        });
        setIsLoading(false);
        return;
      }

      if (state.column === column) {
        const { data } = await axiosInstance.get("/swap_actions", {
          params: {
            ...params,
            sorted_by: column ? column : state.column,
            order: state.direction === "ascending" ? "DESC" : "ASC",
          },
        });
        setstate({
          ...state,
          data: data.data,
          count: data.count,
          pages: data.pages,
          direction:
            state.direction === "ascending" ? "descending" : "ascending",
        });
        setIsLoading(false);
        return;
      }
      const { data } = await axiosInstance.get("/swap_actions", {
        params: {
          ...params,
          sorted_by: column,
          order: state.direction === "ascending" ? "DESC" : "ASC",
        },
      });
      setstate({
        ...state,
        column: column ? column : state.column,
        data: data.data,
        count: data.count,
        pages: data.pages,
        direction: "ascending",
      });

      setIsLoading(false);
    } catch (error) {
      handelError(error);
    }
  };

  const fetchSwapSummaryReport = async (station_id) => {
    try {
      setFetchingSwapSummaryReport(true);
      const { data } = await axiosInstance.get("/swap_action_report", {
        params: {
          state: swap_state,
          created_by: selectedUsername,
          user_group_name: selectedUserGroupName,
          battery_type,
          station: station_id || sp_serial_number,
          lower: lower ? moment(lower, "DD/MM/YYYY").toDate().getTime() : null,
          upper: upper
            ? moment(upper, "DD/MM/YYYY").toDate().getTime() + 86400000
            : null,
          location_id: location,
        },
      });

      setBatteryChargingStatistic(data.data);
    } catch (error) {
      handelError(error);
    } finally {
      setFetchingSwapSummaryReport(false);
    }
  };

  const loadMore = async (station_id) => {
    setIsLoadingLoadMore(true);
    try {
      const { data } = await axiosInstance.get("/swap_actions", {
        params: {
          limit: 50,
          state: swap_state,
          created_by: selectedUsername,
          user_group_name: selectedUserGroupName,
          battery_type,
          station: sp_serial_number ? sp_serial_number : station_id,
          lower: lower ? moment(lower, "DD/MM/YYYY").toDate().getTime() : null,
          upper: upper
            ? moment(upper, "DD/MM/YYYY").toDate().getTime() + 86400000
            : null,
          page: state.page + 1,
        },
      });
      setstate({
        ...state,
        data: [...state.data, ...data.data],
        page: data.page,
      });
    } catch (error) {
      handelError(error);
    } finally {
      setIsLoadingLoadMore(false);
    }
  };

  const lastAxiosInstanceRef = useRef(null);
  useEffect(() => {
    if (!axiosInstance || axiosInstance === lastAxiosInstanceRef.current) return;
    lastAxiosInstanceRef.current = axiosInstance;
    fetchSwaps();
    fetchSwapSummaryReport();
  }, [axiosInstance]);

  const fetchExportData = async (station_id) => {
    try {
      setSwapExportIsLoading(true);
      const { data } = await axiosInstance.get("/swap_actions", {
        params: {
          include_count: false,
          state: swap_state,
          created_by: selectedUsername,
          user_group_name: selectedUserGroupName,
          battery_type,
          station: sp_serial_number ? sp_serial_number : station_id,
          export: true,
          lower: lower ? moment(lower, "DD/MM/YYYY").toDate().getTime() : null,
          upper: upper
            ? moment(upper, "DD/MM/YYYY").toDate().getTime() + 86400000
            : null,
        },
      });

      setExportData(
        data.data.map(
          ({
            battery_type,
            count_in,
            count_out,
            created_at,
            created_by,
            dynamo_swap_id,
            location,
            location_id,
            requested,
            state,
            station,
            updated_at,
            user_group_name,
            tracking_in,
            tracking_out,
          }) => {
            return {
              booking_id: dynamo_swap_id,
              sp_serial_number: station,
              user_name: created_by,
              battery_count: requested,
              count_in: count_in ? count_in : 0,
              count_out: count_out ? count_out : 0,
              count_in_new_logic: tracking_in ? tracking_in : 0,
              count_out_new_logic: tracking_out ? tracking_out : 0,
              timestamp: moment(created_at).format("DD-MM-YYYY HH:mm:ss"),
              state,
              user_group_name,
              updated_at,
              location,
              location_id,
              battery_type,
            };
          }
        )
      );
      setSwapExportIsLoading(false);
    } catch (error) {
      toast.error(
        "Response size exceeds export limit. Apply filters to reduce size."
      );
      setExportData([]);
      setSwapExportIsLoading(false);
    }
  };

  const resetFilterButtonHandler = () => {
    setBatteryStateSearch(null);
    setSelectedLocation(null);
    setSelectedLocation(null);
    setSelectedUserGroupName(null);
    setSelectedBatteryType(null);
    setSelectedStation(null);
    setSelectedUsername(null);
    setUpper(null);
    setLower(null);
  };

  let summary_report_new_lib = useMemo(() => {
    if (!batteryChargingStatistic) return;
    let res = Object.keys(batteryChargingStatistic).map((key) => {
      if (batteryChargingStatistic[key].sum_count_out)
        return {
          label: key,
          [key]: batteryChargingStatistic[key].sum_count_out,
          [`${key}Color`]: "hsl(121, 70%, 50%)",
        };
    });
    return res.filter(function (item) {
      return item !== undefined;
    });
  }, [batteryChargingStatistic]);

  const fetchSwapById = async (swapId) => {
    try {
      const { data } = await api.get("/swap/" + swapId);
      setSwap(data.data);
    } catch (error) {
      handelError(error);
      navigate("/swaps");
    }
  };
  const abortSwap = async (swapId) => {
    try {
      setAbortSwapLoading(true);
      await api.put(`/swaps/${swapId}/abort`);
      setSwap((prev) => ({ ...prev, state: "ABORTED" }));
      toast.success("Successfully aborted swap");
    } catch (error) {
      console.error("Error aborting swap:", error);
      toast.error("Failed to abort swap. Please try again.");
    } finally {
      setAbortSwapLoading(false);
    }
  };

  return (
    <SwapContext.Provider
      value={{
        abortSwap,
        swap,
        fetchSwapById,
        abortSwapLoading,
        setSwap,
        summary_report_new_lib,
        resetFilterButtonHandler,
        fetchExportData,
        loadMore,
        isLoadingLoadMore,
        isLoading,
        exportData,
        lower,
        upper,
        location,
        IsSwapExportIsLoading,
        setSwapExportIsLoading,
        fetchSwaps,
        setLower,
        setSelectedLocation,
        setUpper,
        state,
        selectedUserGroupName,
        setSelectedUserGroupName,
        setSelectedStation,
        sp_serial_number,
        setSelectedBatteryType,
        battery_type,
        swap_state,
        setBatteryStateSearch,
        selectedUsername,
        setSelectedUsername,
        fetchSwapSummaryReport,
        fetchingSwapSummaryReport,
      }}
    >
      {children}
    </SwapContext.Provider>
  );
};
